/**
 * AppTopNav — desktop top-navigation bar (≥1024px).
 *
 * Left, near the edge:  Tago logo + Explore / Tours / Trips / Messages
 * Right, near the edge: HostingModeToggle · bell · avatar → /profile
 *
 * Full viewport width. A centered 1440 column left fat empty gutters on
 * wide monitors — the two clusters must sit close to the margins, with
 * space between the groups, not a marketing-hero maxWidth.
 *
 * Mounted by AppShell only on desktop. Mobile keeps the Expo Router tab bar.
 */
import { useReducedMotion } from 'react-native-reanimated'
import React, { useMemo, useState } from 'react'
import { View, Platform } from 'react-native'
import { useRouter, usePathname } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Image } from 'expo-image'
import { Bell } from '@/components/ui/icons'
import { Pressable } from '../ui/Pressable'
import { Avatar } from '../ui/Avatar'
import { TopNavLink } from './TopNavLink'
import { HostingModeToggle } from './HostingModeToggle'
import { env } from '../../lib/env'
import { useAuth } from '../../lib/hooks/use-auth'
import { useTheme } from '../../lib/hooks/use-theme'
import { useNotificationsUnreadCount } from '../../lib/hooks/use-notifications-unread'
import { getCurrentProfileDisplay } from '../../lib/utils/profile-display'
import { useSemanticColors, DURATION } from '../../lib/theme/tokens'
import { COLORS, SPACING, RADIUS } from '../../lib/brand'
import { APP_TOP_NAV_HEIGHT } from '../../lib/theme/chrome'
import { CountBadge } from '../ui/CountBadge'
import { GlassSurface } from '../ui/GlassSurface'

const isWeb = Platform.OS === 'web'

export function AppTopNav() {
  const reducedMotion = useReducedMotion()
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname() ?? ''
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user, profile } = useAuth()
  const unreadCount = useNotificationsUnreadCount()
  const [bellHover, setBellHover] = useState(false)
  const [avatarHover, setAvatarHover] = useState(false)
  const profileDisplay = useMemo(() => getCurrentProfileDisplay({ profile, user }), [profile, user])

  const isActive = (route: string) => pathname === route || pathname.startsWith(`${route}/`)

  return (
    <GlassSurface
      tone="regular"
      visualRole="chrome"
      radius={0}
      style={{
        // El alto sale de `lib/theme/chrome.ts` porque no es solo el alto de
        // esta barra: es lo que el host de toasts tiene que esquivar para no
        // pararse encima de la navegacion. Dos numeros para una misma geometria
        // ya se desincronizaron una vez.
        height: APP_TOP_NAV_HEIGHT,
        ...(isWeb
          ? ({
              position: 'sticky' as unknown as 'absolute',
              top: 0,
              zIndex: 50,
              backdropFilter: 'blur(18px)',
            } as unknown as object)
          : null),
      }}
      contentStyle={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: SPACING.lg,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <Pressable
          onPress={() => router.navigate('/explore' as never)}
          accessibilityRole="link"
          accessibilityLabel={t('common.app_name')}
          testID="topnav.logo"
          style={{ flexDirection: 'row', alignItems: 'center' }}
        >
          <Image
            source={require('../../assets/images/logo-horizontal.png')}
            style={{ width: 96, height: 28 }}
            contentFit="contain"
            accessibilityLabel={t('common.app_name')}
          />
        </Pressable>

        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TopNavLink
            label={t('tabs.explore')}
            href="/explore"
            active={isActive('/explore') || pathname === '/'}
            testID="topnav.explore"
          />
          <TopNavLink
            label={t('tabs.services')}
            href="/services"
            active={isActive('/services')}
            testID="topnav.services"
          />
          {env.isToursEnabled ? (
            <TopNavLink
              label={t('tabs.tours')}
              href="/tours"
              active={isActive('/tours')}
              testID="topnav.tours"
            />
          ) : null}
          <TopNavLink
            label={t('tabs.trips')}
            href="/trips"
            active={isActive('/trips')}
            testID="topnav.trips"
          />
          <TopNavLink
            label={t('tabs.messages')}
            href="/messages"
            active={isActive('/messages')}
            badge={unreadCount}
            testID="topnav.messages"
          />
        </View>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <HostingModeToggle />

        <Pressable
          onPress={() => router.push('/profile/notifications' as never)}
          onHoverIn={() => setBellHover(true)}
          onHoverOut={() => setBellHover(false)}
          accessibilityRole="button"
          accessibilityLabel={t('explore.notifications')}
          testID="topnav.notifications"
          style={{
            width: 40,
            height: 40,
            alignItems: 'center',
            justifyContent: 'center',
            ...(isWeb
              ? ({
                  transition: reducedMotion
                    ? undefined
                    : `background-color ${DURATION.fast}ms ease-out, border-color ${DURATION.fast}ms ease-out, transform ${DURATION.fast}ms ease-out`,
                  transform: bellHover ? 'translateY(-1px)' : 'translateY(0)',
                } as unknown as object)
              : null),
          }}
        >
          <GlassSurface
            tone={bellHover ? 'tint' : 'regular'}
            tintColor={bellHover ? COLORS.coral.light : undefined}
            visualRole="chrome"
            interactive
            radius={RADIUS.full}
            style={{ width: 40, height: 40, position: 'relative' }}
            contentStyle={{ alignItems: 'center', justifyContent: 'center' }}
          >
            <Bell size={18} color={colors.textPrimary} strokeWidth={1.8} />
            {unreadCount > 0 && (
              <View
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                }}
              >
                <CountBadge count={unreadCount} ringColor={colors.background} />
              </View>
            )}
          </GlassSurface>
        </Pressable>

        <Pressable
          onPress={() => router.navigate('/profile' as never)}
          onHoverIn={() => setAvatarHover(true)}
          onHoverOut={() => setAvatarHover(false)}
          accessibilityRole="link"
          accessibilityLabel={t('tabs.profile')}
          testID="topnav.avatar"
          style={{
            borderRadius: RADIUS.full,
            borderWidth: 2,
            borderColor: avatarHover ? `${COLORS.coral.DEFAULT}66` : colors.border,
            ...(isWeb
              ? ({
                  transition: reducedMotion
                    ? undefined
                    : `border-color ${DURATION.fast}ms ease-out, transform ${DURATION.fast}ms cubic-bezier(0.16, 1, 0.3, 1)`,
                  transform: avatarHover ? 'translateY(-1px)' : 'translateY(0)',
                } as unknown as object)
              : null),
          }}
        >
          <Avatar
            size="sm"
            source={profileDisplay.avatarUrl}
            blurhash={profileDisplay.avatarBlurhash ?? undefined}
            fallback={profileDisplay.fallback}
            colorSeed={profileDisplay.id}
            accessibilityLabel={profileDisplay.accessibilityLabel || t('tabs.profile')}
          />
        </Pressable>
      </View>
    </GlassSurface>
  )
}
